import axios, { AxiosInstance, AxiosError } from "axios";
import { apiBaseUrl } from "@/config/app";
import { resolveClientApiUrl } from "@/lib/api/apiRouting";
import {
  getAccessToken,
  getRefreshToken,
  setCookies,
  clearCookies,
} from "./cookie-utils";
import { useAuthStore } from "@/store/auth";
import { useAuthModalStore } from "@/store/authModal";
import { useAssociationStore } from "@/store/association";

declare global {
  interface Window {
    __JOOD_ASSOCIATION_ID__?: number;
  }
}

// NOTE: API response caching is intentionally handled at the service layer (e.g. `cachedGet`),
// not inside the shared axios client, to avoid hidden/duplicated caching behavior here.
//
// Timeout: default 30s for heavy operations (payments, cart sync). For session/layout-style
// calls that go through Next.js API routes, use clientGet() with timeoutMs (e.g. SESSION_LAYOUT_TIMEOUT_MS)
// so slow networks get a retry UX instead of long hangs. Per-request timeout can override via config.timeout.

// In-memory token cache for faster access (synced with cookies)
const tokenCache: { accessToken: string | null; refreshToken: string | null } =
  {
    accessToken: null,
    refreshToken: null,
  };

// Function to update token cache (called from auth store)
export function updateTokenCache(
  accessToken: string | null,
  refreshToken: string | null = null,
) {
  tokenCache.accessToken = accessToken;
  if (refreshToken !== null) {
    tokenCache.refreshToken = refreshToken;
  }
}

// Initialize token cache from cookies on client-side
if (typeof window !== "undefined") {
  getAccessToken().then((token) => {
    tokenCache.accessToken = token;
  });
  getRefreshToken().then((token) => {
    tokenCache.refreshToken = token;
  });
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: apiBaseUrl,
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": process.env.NEXT_PUBLIC_API_KEY || "", // Add API key to all requests
      },
      // Default 30s for heavy ops; session/layout use clientGet with shorter timeout (see clientApi).
      timeout: 30_000,
    });

    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        // Path-aware routing: legacy / websites / ERP (matches serverApiRouting).
        if (typeof window !== "undefined" && config.url) {
          const requestPath = config.url.startsWith("/") ? config.url : `/${config.url}`;
          const { url } = resolveClientApiUrl(window.location.origin, requestPath);
          config.baseURL = "";
          config.url = url;
        }

        // Ensure API key is always included
        if (!config.headers["X-API-Key"]) {
          config.headers["X-API-Key"] = process.env.NEXT_PUBLIC_API_KEY || "";
        }

        // Add auth token from cache (synced with cookies).
        // On hard refresh there is a short race where token cache may still be empty,
        // so fallback to reading cookies before first protected request.
        if (typeof window !== "undefined") {
          if (!tokenCache.accessToken) {
            const cookieAccessToken = await getAccessToken();
            if (cookieAccessToken) {
              tokenCache.accessToken = cookieAccessToken;
            }
          }
          if (!tokenCache.refreshToken) {
            const cookieRefreshToken = await getRefreshToken();
            if (cookieRefreshToken) {
              tokenCache.refreshToken = cookieRefreshToken;
            }
          }
          if (tokenCache.accessToken) {
            config.headers.Authorization = `Bearer ${tokenCache.accessToken}`;
          }
        }

        // Portal context: send association context via header when the store has one.
        // Source of truth: association store (set from server-rendered association data).
        if (typeof window !== "undefined") {
          const storeId = useAssociationStore.getState().associationId;
          const serverResolvedId = window.__JOOD_ASSOCIATION_ID__;
          const associationId =
            storeId && storeId > 0
              ? storeId
              : typeof serverResolvedId === "number" &&
                  Number.isFinite(serverResolvedId) &&
                  serverResolvedId > 0
                ? Math.trunc(serverResolvedId)
                : null;
          if (associationId && !config.headers["X-Association-Id"]) {
            config.headers["X-Association-Id"] = String(associationId);
          }
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response.data;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as typeof error.config & {
          _retry?: boolean;
        };

        // For logout: 401 means already logged out or token invalid — treat as success so caller can clear local state
        const isLogoutRequest =
          originalRequest?.url?.includes("/auth/logout") ?? false;
        if (error.response?.status === 401 && isLogoutRequest) {
          return Promise.resolve({ data: {} });
        }

        // Handle 401 errors with token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (typeof window !== "undefined") {
            // Get refresh token from cache or cookies
            let refreshToken = tokenCache.refreshToken;
            if (!refreshToken) {
              refreshToken = await getRefreshToken();
              if (refreshToken) {
                tokenCache.refreshToken = refreshToken;
              }
            }

            if (refreshToken) {
              try {
                // Attempt to refresh the token
                const refreshUrl =
                  typeof window !== "undefined"
                    ? resolveClientApiUrl(window.location.origin, "/auth/refresh").url
                    : `${apiBaseUrl}/auth/refresh`;
                const refreshResponse = await axios.post(
                  refreshUrl,
                  { refreshToken },
                  {
                    headers: {
                      "Content-Type": "application/json",
                      "X-API-Key": process.env.NEXT_PUBLIC_API_KEY || "",
                    },
                  },
                );

                // Backend may return tokens at data.accessToken or data.tokens.accessToken
                const data = refreshResponse.data?.data as
                  | Record<string, unknown>
                  | undefined;
                const tokens =
                  (data?.tokens as Record<string, unknown>) ?? data;
                const newAccessToken =
                  (data?.accessToken as string) ||
                  (tokens?.accessToken as string) ||
                  (refreshResponse.data?.accessToken as string);
                const newRefreshToken =
                  (data?.refreshToken as string) ||
                  (tokens?.refreshToken as string) ||
                  (refreshResponse.data?.refreshToken as string);

                if (newAccessToken) {
                  // Update token cache
                  updateTokenCache(newAccessToken, newRefreshToken);

                  // Update cookies
                  await setCookies({
                    accessToken: newAccessToken,
                    refreshToken: newRefreshToken,
                  });

                  // Update the original request with new token
                  if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                  }

                  // Retry the original request
                  return this.client(originalRequest);
                }
              } catch {
                // Refresh failed: clear auth state and open login popup in place
                await useAuthStore.getState().clearAuth();
                useAuthModalStore.getState().open();
              }
            } else {
              // No refresh token, clear everything and open login popup in place
              await useAuthStore.getState().clearAuth();
              useAuthModalStore.getState().open();
            }
          }
        }

        return Promise.reject(error);
      },
    );
  }

  getClient(): AxiosInstance {
    return this.client;
  }
}

export const apiClient = new ApiClient().getClient();
export default apiClient;

